import React, { useState, useRef } from 'react';
import {
    Box, Button, Typography, CircularProgress,
    Alert, Chip, Divider, LinearProgress, Tooltip, IconButton, Paper, Stack
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_BASE_URL } from '../components/api.js';
import { useAuth } from '../components/AuthContext';

// ─── Caixa de upload individual ───────────────────────────────────────────────
function FileBox({ label, descricao, accept, file, onSelect, onClear, cor }) {
    const ref = useRef();

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files[0];
        if (f) onSelect(f);
    };

    return (
        <Paper
            variant="outlined"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            sx={{
                flex: 1,
                p: 3,
                minWidth: 220,
                borderColor: file ? cor : 'divider',
                borderWidth: 2,
                borderStyle: file ? 'solid' : 'dashed',
                cursor: 'pointer',
                textAlign: 'center',
                '&:hover': { borderColor: cor },
            }}
            onClick={() => !file && ref.current.click()}
        >
            <input
                ref={ref}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={(e) => onSelect(e.target.files[0])}
            />

            {file ? (
                <Stack spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 48, color: cor }} />
                    <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ width: '100%' }}>
                        {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                    <Tooltip title="Remover arquivo">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onClear(); }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ) : (
                <Stack spacing={1} alignItems="center">
                    <UploadFileIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {descricao}
                    </Typography>
                </Stack>
            )}
        </Paper>
    );
}

// ─── Página principal ──────────────────────────────────────────────────────────
export default function PagamentosCsvXlsx() {
    const [arquivo1, setArquivo1] = useState(null);
    const [arquivo2, setArquivo2] = useState(null);
    const [arquivo3, setArquivo3] = useState(null);
    const [arquivo4, setArquivo4] = useState(null);

    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [erro, setErro] = useState(null);
    const [limpando, setLimpando] = useState(false);

    const { authenticatedFetch, logout } = useAuth();

    const prontoParaEnviar = arquivo1 && arquivo2 && arquivo3 && arquivo4;

    const handleProcessar = async () => {
        if (!prontoParaEnviar) return;
        setLoading(true);
        setResultado(null);
        setErro(null);

        const formData = new FormData();
        formData.append('arquivo1', arquivo1);
        formData.append('arquivo2', arquivo2);
        formData.append('arquivo3', arquivo3);
        formData.append('arquivo4', arquivo4);

        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/pagamento-ler-csv-xlsx-processar/`, {
                method: "POST",
                body: formData,
                headers: {}
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.erro || `Erro HTTP: ${response.status}`);
            }

            const data = await response.json();
            setResultado(data);
            setArquivo1(null);
            setArquivo2(null);
            setArquivo3(null);
            setArquivo4(null);
        } catch (e) {
            console.error('Erro no processamento:', e);
            if (e.message.includes('Token')) {
                logout();
            } else {
                setErro(e.message || 'Erro ao processar arquivos.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLimpar = async () => {
        if (!window.confirm('Apagar TODOS os registros do banco? Esta ação não pode ser desfeita.')) return;
        setLimpando(true);
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/pagamento-ler-csv-xlsx-limpar/`, {
                method: "DELETE"
            });

            if (!response.ok) throw new Error("Erro ao limpar registros.");

            const data = await response.json();
            setResultado({ mensagem: data.mensagem });
        } catch (e) {
            setErro(e.message);
        } finally {
            setLimpando(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
            {/* Cabeçalho */}
            <Paper elevation={0} sx={{ mb: 3, p: 2, backgroundColor: 'transparent' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Importar Pagamentos</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Carregue os 4 arquivos e clique em <strong>Processar</strong> para iniciar a apuração
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={handleLimpar}
                        disabled={limpando}
                    >
                        {limpando ? 'Limpando...' : 'Limpar banco'}
                    </Button>
                </Stack>
            </Paper>

            {/* Caixas de upload */}
            <Stack direction="row" gap={2} flexWrap="wrap" mb={3}>
                <FileBox
                    label="Arquivo 1 – SW"
                    descricao="Relatório Vendas (.csv)"
                    accept=".csv"
                    cor="#1976d2"
                    file={arquivo1}
                    onSelect={setArquivo1}
                    onClear={() => setArquivo1(null)}
                />
                <FileBox
                    label="Arquivo 2 – BIN Cartões"
                    descricao="Cartões Detalhado (.csv)"
                    accept=".csv"
                    cor="#388e3c"
                    file={arquivo2}
                    onSelect={setArquivo2}
                    onClear={() => setArquivo2(null)}
                />
                <FileBox
                    label="Arquivo 3 – BIN PIX"
                    descricao="PIX – BIN (.xlsx)"
                    accept=".xlsx,.xls"
                    cor="#f57c00"
                    file={arquivo3}
                    onSelect={setArquivo3}
                    onClear={() => setArquivo3(null)}
                />
                <FileBox
                    label="Arquivo 4 – Voucher"
                    descricao="Vouchers Detalhado (.csv)"
                    accept=".csv"
                    cor="#7b1fa2"
                    file={arquivo4}
                    onSelect={setArquivo4}
                    onClear={() => setArquivo4(null)}
                />
            </Stack>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Ações */}
            <Stack spacing={2} alignItems="flex-start">
                <Button
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                    disabled={!prontoParaEnviar || loading}
                    onClick={handleProcessar}
                >
                    {loading ? 'Processando...' : 'Processar Arquivos'}
                </Button>

                {!prontoParaEnviar && !loading && (
                    <Typography variant="caption" color="text.secondary">
                        Aguardando carregamento dos 4 arquivos obrigatórios.
                    </Typography>
                )}

                {/* Resultados */}
                {resultado && (
                    <Alert severity="success" sx={{ width: '100%' }}>
                        <Typography fontWeight={600} mb={1}>{resultado.mensagem}</Typography>
                        {resultado.total !== undefined && (
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                <Chip label={`Total: ${resultado.total}`} size="small" />
                                <Chip label={`Com match: ${resultado.com_match}`} color="success" size="small" />
                                <Chip label={`Sem match: ${resultado.sem_match}`} color="warning" size="small" />
                            </Stack>
                        )}
                    </Alert>
                )}

                {erro && <Alert severity="error" sx={{ width: '100%' }}>{erro}</Alert>}
            </Stack>

            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" color="text.secondary">
                <strong>Regra de apuração:</strong> Valor igual e diferença de até 6 minutos entre SW e BIN (cartão, PIX e voucher).
            </Typography>
        </Box>
    );
}